import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../Logo/CompanyLogo.png";
import Seal from '../Seal/Seal.png';

export const generateInvoiceQuotationPDF = (quotationData, companyData = null) => {
  const doc = new jsPDF("p", "pt", "a4");

  const BLUE = [0, 51, 102];
  const MARGIN = 40;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    // Handle invalid date input
    if (isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = String(date.getFullYear()).slice(-2);

    return `${day}-${month}-${year}`;
  };

  // Helper function to normalize Unicode fractions to regular text
  const normalizeText = (text) => {
    if (!text) return "";
    
    // Convert Unicode fraction characters to regular text equivalents
    const fractionMap = {
      '\u00BD': '1/2',  // ½
      '\u2153': '1/3',  // ⅓
      '\u2154': '2/3',  // ⅔
      '\u00BC': '1/4',  // ¼
      '\u00BE': '3/4',  // ¾
      '\u2155': '1/5',  // ⅕
      '\u2156': '2/5',  // ⅖
      '\u2157': '3/5',  // ⅗
      '\u2158': '4/5',  // ⅘
      '\u2159': '1/6',  // ⅙
      '\u215A': '5/6',  // ⅚
      '\u2150': '1/7',  // ⅐
      '\u215B': '1/8',  // ⅛
      '\u215C': '3/8',  // ⅜
      '\u215D': '5/8',  // ⅝
      '\u215E': '7/8',  // ⅞
      '\u2044': '/',    // fraction slash
      '\u2215': '/',    // division slash
    };
    
    let result = String(text);
    
    // Replace all Unicode fractions with regular text
    Object.keys(fractionMap).forEach(unicode => {
      result = result.replace(new RegExp(unicode, 'g'), fractionMap[unicode]);
    });
    
    return result;
  };

  // ============================================================
  // 🔹 HEADER (appears on every page)
  // ============================================================
  const addHeader = (doc) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 25;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("INVOICE", MARGIN, y);

    y += 20;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text((quotationData.InvoicePrint?.WorkName || "").toUpperCase(), MARGIN, y);

    y += 14;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Faman Services & Solutions®", MARGIN, y);

    // ✅ Dynamic logo size from company data or use default
    const logoWidth = companyData?.LogoWidth || 150;
    const logoHeight = companyData?.LogoHeight || 60;
    const logoX = pageWidth - logoWidth - 30; // 30px margin from right
    const logoY = 35;

    doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    // ✅ Set font to Arial-like (Helvetica)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    // ✅ Build company info from API data or use default
    // const COMPANY_INFO = companyData ? [
    //   `${companyData.Address1 || ''}${companyData.Address2 ? ', ' + companyData.Address2 : ''}`,
    //   `${companyData.City || ''}, ${companyData.State || ''} – ${companyData.Pincode || ''}, ${companyData.Country || ''}`,
    //   `Email: ${companyData.Email || ''} | Website: ${companyData.Website || ''} | Ph No.: ${companyData.PhoneNumber || ''}`,
    //   `GSTIN: ${companyData.GSTNumber || ''} | STATE: ${companyData.State || ''} | STATE CODE: ${companyData.StateCode || ''}`,
    //   `PAN NO.: ${companyData.PANNumber || ''} | SERVICE TAX NO.: ${companyData.ServiceTaxNumber || ''} | TIN: ${companyData.TINNumber || ''}`,
    // ] : [
    //   "43/1582 ,Ground Floor,Kochapilly Road,Palarivattom,",
    //   "Ernakulam, Kerala – 682025, India",
    //   "Email: support@faman.in | Website: www.faman.in | Ph No.: 9633940163",
    //   "GSTIN: 32AAFFF1487P1Z3 | STATE: KERALA | STATE CODE: 32",
    //   "PAN NO.: AAFFF1487P | SERVICE TAX NO.: AAFFF1487PSD001 | TIN: 32071281568",
    // ];

    const COMPANY_INFO = [
      // "43/1582, Ground Floor, Kochalipady Road, Palarivattom,",
      "43/1582 ,Ground Floor,Kochapilly Road,Palarivattom,",
      "Ernakulam, Kerala – 682025, India",
      "Email: support@faman.in | Website: www.faman.in | Ph No.: 9633940163",
      "GSTIN: 32AAFFF1487P1Z3 | STATE: KERALA | STATE CODE: 32",
      "PAN NO.: AAFFF1487P | SERVICE TAX NO.: AAFFF1487PSD001 | TIN: 32071281568",
    ];
    y += 15;

    // ✅ Print each line with custom spacing (auto gap between lines)
    COMPANY_INFO.forEach((line, index) => {
      doc.text(line, MARGIN, y);
      // 👇 Increase spacing between lines (adjust this for more/less gap)
      y += 13; // try 11 for tight spacing, 14 for moderate, 16 for wider gap
    });

    y += 8;
    doc.setLineWidth(0.5);
    doc.line(MARGIN, y, pageWidth - MARGIN, y);

    y += 15;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("INVOICE", pageWidth / 2, y, { align: "center" });

    return y + 18; // returns space after header
  };

  // ============================================================
  // 🔹 FIRST PAGE CONTENT (Customer + Info)
  // ============================================================
  let y = addHeader(doc);
  const pageWidth = doc.internal.pageSize.getWidth();

  const billedToStartY = y; // Save the starting Y position

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("BILLED TO:", MARGIN, y);
  y += 12;
  doc.setFont("helvetica", "normal");
  doc.text(quotationData.InvoicePrint.CustomerName, MARGIN, y);
  y += 12;
  doc.setFontSize(8);
  if (quotationData.InvoicePrint.CustomerAddress1) {
    doc.text(quotationData.InvoicePrint.CustomerAddress1, MARGIN, y);
    y += 12;
  }
   if (quotationData.InvoicePrint.CustomerAddress2) {
    doc.text(quotationData.InvoicePrint.CustomerAddress2, MARGIN, y);
    y += 12;
  }
  doc.text(quotationData.InvoicePrint.CustomerLocation, MARGIN, y);
  y += 12;
  if (quotationData.InvoicePrint.CustomerGSTNumber) {
    doc.text("GST No: " + quotationData.InvoicePrint.CustomerGSTNumber, MARGIN, y);
  }

  const infoX = pageWidth - 250;

  const details = [
    ["Invoice Number", quotationData.InvoicePrint.InvoiceBillNumber],
    ["Invoice Date", formatDate(quotationData?.InvoicePrint?.InvoiceDate) || "N/A"],
    ["Site", quotationData?.InvoicePrint?.CustomerLocation || ""],
  ];

  let infoY = billedToStartY; // Start at the same Y position as "BILLED TO:"

  // Find the longest label to align all values at the same position
  doc.setFont("helvetica", "bold");
  const maxLabelWidth = Math.max(...details.map(([label]) => doc.getTextWidth(label)));
  const valueStartX = infoX + maxLabelWidth + 10; // 10px gap after longest label

  details.forEach(([label, value]) => {
    doc.setFont("helvetica", "bold");
    doc.text(label, infoX, infoY);

    doc.setFont("helvetica", "normal");
    doc.text(value, valueStartX, infoY);
    infoY += 15;
  });

  y += 40;

  // ============================================================
  // 🔹 MAIN ITEMS TABLE (with automatic header)
  // ============================================================
  const tableData = quotationData.InvoicePrintList || [
    {
      description: "",
      uom: "",
      qty: "",
      unitPrice: "",
      taxableValue: "",
      gstrate: "",
      cgst: "",
      sgst: "",
      totalAmount: "",
    },
  ];

  const isIGST = tableData.some(item => (item.IGSTAmount ?? 0) > 0);

  // Calculate totals for the summary row
  const totalTaxable = tableData.reduce((sum, item) => sum + (item.TaxableAmount ?? 0), 0);
  const totalCGST = tableData.reduce((sum, item) => sum + (item.CGSTAmount ?? 0), 0);
  const totalSGST = tableData.reduce((sum, item) => sum + (item.SGSTAmount ?? 0), 0);
  const totalIGST = tableData.reduce((sum, item) => sum + (item.IGSTAmount ?? 0), 0);
  const totalAmount = tableData.reduce((sum, item) => sum + (item.TotalAmount ?? 0), 0);

  // Build table body with data rows + Total row
  const tableBody = [
    // Map all data rows from response
    ...tableData.map((item, i) => {
      // Build description: WorkItemName / WorkDetailsName (ItemRemark)
      let description = "";
      
      // Add WorkItemName if available
      if (item.WorkItemName) {
        description = normalizeText(item.WorkItemName);
      }
      
      // Add WorkDetailsName if available
      if (item.WorkDetailsName || item.Description) {
        description += (description ? " / " : "") + normalizeText(item.WorkDetailsName || item.Description);
      }
      
      // Add ItemRemark if it exists - normalize Unicode fractions to regular text
      if (item.ItemRemark && item.ItemRemark.trim() !== "") {
        const remarkText = normalizeText(item.ItemRemark);
        description += ` (${remarkText})`;
      }
      
      return isIGST
        ? [
          i + 1,
          description,
          item.HSCODE,
          item.QAUOM,
          1,
          (item.TaxableAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
          (item.TaxableAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
          (item.GSTRate ?? 0).toLocaleString("en-IN"),
          (item.IGSTAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
          (item.TotalAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        ]
        : [
          i + 1,
          description,
          item.HSCODE,
          item.QAUOM,
          1,
          (item.TaxableAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
          (item.TaxableAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
          (item.GSTRate ?? 0).toLocaleString("en-IN"),
          (item.CGSTAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
          (item.SGSTAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
          (item.TotalAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        ];
    }),
    // Add Total row at the end
    isIGST
      ? [
        "",
        "Total:",
        "",
        "",
        "",
        "",
        totalTaxable.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        "",
        totalIGST.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      ]
      : [
        "",
        "Total:",
        "",
        "",
        "",
        "",
        totalTaxable.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        "",
        totalCGST.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        totalSGST.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      ]
  ];

  autoTable(doc, {
    startY: y,
    theme: "grid",
    margin: { top: 160, left: 20, right: 20, bottom: 60 },
    head: [
      isIGST
        ? ["Sl", "Description", "HSN/SAC", "UOM", "Qty", "Unit Price", "Taxable", "GST%", "IGST", "Total"]
        : ["Sl", "Description", "HSN/SAC", "UOM", "Qty", "Unit Price", "Taxable", "GST%", "CGST", "SGST", "Total"],
    ],
    body: tableBody,
    headStyles: {
      fillColor: BLUE,
      textColor: [255, 255, 255],
      fontSize: 8,
      halign: "left",
      valign: "middle",
      cellPadding: 2,
    },
    bodyStyles: {
      fontSize: 8,
      textColor: [0, 0, 0],
      cellPadding: 2,
    },
    // 👉 Align numeric columns to the right
    columnStyles: isIGST
      ? {
        0: { halign: "right", cellWidth: 25 }, // Sl - right
        1: { halign: "left", cellWidth: 'auto' },   // Description - left
        2: { halign: "center", cellWidth: 50 }, // HSN - center with fixed width
        3: { halign: "center", cellWidth: 35 }, // UOM - center
        4: { halign: "right", cellWidth: 30 },  // Qty
        5: { halign: "right", cellWidth: 60 },  // Unit Price
        6: { halign: "right", cellWidth: 60 },  // Taxable
        7: { halign: "right", cellWidth: 35 },  // GST %
        8: { halign: "right", cellWidth: 60 },  // IGST
        9: { halign: "right", cellWidth: 70 },  // Total
      }
      : {
        0: { halign: "right", cellWidth: 25 }, // Sl - right
        1: { halign: "left", cellWidth: 'auto' },   // Description - left
        2: { halign: "center", cellWidth: 50 }, // HSN - center with fixed width
        3: { halign: "center", cellWidth: 35 }, // UOM - center
        4: { halign: "right", cellWidth: 30 },  // Qty
        5: { halign: "right", cellWidth: 60 },  // Unit Price
        6: { halign: "right", cellWidth: 60 },  // Taxable
        7: { halign: "right", cellWidth: 35 },  // GST %
        8: { halign: "right", cellWidth: 55 },  // CGST
        9: { halign: "right", cellWidth: 55 },  // SGST
        10: { halign: "right", cellWidth: 70 }, // Total
      },
    didParseCell: (data) => {
      // Right-align headers for numeric columns
      if (data.section === 'head') {
        const numericColumns = isIGST ? [0, 4, 5, 6, 7, 8, 9] : [0, 4, 5, 6, 7, 8, 9, 10];
        if (numericColumns.includes(data.column.index)) {
          data.cell.styles.halign = "right";
        }
      }
      
      // Style the "Total:" row (last row)
      if (data.section === 'body' && data.row.index === tableBody.length - 1) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fontSize = 10; // Larger font size for Total row
        data.cell.styles.fillColor = [220, 220, 220]; // light grey background

        // Right-align all columns except Sl and Description in Total row
        if (data.column.index >= 2) {
          data.cell.styles.halign = "right";
        }
      }
    },
  });


  let afterTableY = doc.lastAutoTable.finalY + 30;

  // ============================================================
  // 🔹 TOTALS + SUMMARY (New Design)
  // ============================================================
  const totals = quotationData.totals || {
    beforeTax: quotationData.InvoicePrint.TaxableAmount,
    gst: ((parseFloat(quotationData?.InvoicePrint?.CGSTAmount ?? 0) + parseFloat(quotationData?.InvoicePrint?.SGSTAmount ?? 0) + parseFloat(quotationData?.InvoicePrint?.IGSTAmount ?? 0))).toFixed(2),
    afterTax: ((parseFloat(quotationData?.InvoicePrint?.TaxableAmount ?? 0) + parseFloat(quotationData?.InvoicePrint?.CGSTAmount ?? 0) + parseFloat(quotationData?.InvoicePrint?.SGSTAmount ?? 0) + parseFloat(quotationData?.InvoicePrint?.IGSTAmount ?? 0))).toFixed(2),
    roundOff: quotationData.InvoicePrint.RoundOff,
    grandTotal: quotationData.InvoicePrint.TotalAmount,
  };

  // Get GST rate from first item in the table
  const gstRate = quotationData.InvoicePrintList?.[0]?.GSTRate || 0;

  // Calculate Balance Due (Total Amount After Tax + Round Off)
  const balanceDue = Number(totals.afterTax || 0) + Number(totals.roundOff || 0);

  // Build total lines - conditionally show GST rate if > 0
  const totalLines = [
    ["Total Amount Before Tax", `${Number(totals.beforeTax || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`],
    ...(gstRate > 0 ? [[`GST @ ${gstRate}%`, `${Number(totals.gst || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`]] : [["GST", `${Number(totals.gst || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`]]),
    ["Total Amount After Tax", `${Number(totals.afterTax || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`],
    ["Round Off", `${Number(totals.roundOff || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`],
  ];

  // ---------- TOTALS SECTION (Right-aligned with proper spacing) ----------
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10); // Reduced font size

  const labelX = pageWidth - 280; // Moved more to the right
  const valueX = pageWidth - 40;  // right-aligned value column
  const lineHeight = 16; // Slightly reduced line height

  // Print totals neatly aligned
  totalLines.forEach(([label, value], i) => {
    const lineY = afterTableY + i * lineHeight;

    // Label on left
    doc.text(label, labelX, lineY);

    // Value — right aligned (closer to label)
    doc.text(value, valueX, lineY, { align: "right" });
  });

  // Move below total section
  afterTableY += totalLines.length * lineHeight + 10;

  // ---------- BALANCE DUE (Underlined) ----------
  const balanceDueY = afterTableY;
  
  // Draw underline above Balance Due
  doc.setLineWidth(0.5);
  doc.line(labelX, balanceDueY - 5, valueX, balanceDueY - 5);
  
  // Print Balance Due label and value
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Balance Due", labelX, balanceDueY + 10);
  doc.text(quotationData.InvoicePrint.TotalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 }), valueX, balanceDueY + 10, { align: "right" });
  
  // Move below Balance Due section
  afterTableY = balanceDueY + 40;

  // ---------- GRAND TOTAL ----------
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);

  const grandTotalValue = totals.grandTotal || 0;
  const amountInWords =
    quotationData.amountInWords ||
    (convertNumberToWords(Math.floor(grandTotalValue)) || "Zero") + " Rupees Only";

  // Grand total in words — left side
  doc.text(`GRAND TOTAL: ${amountInWords}`, MARGIN, afterTableY);

  // Add "For FAMAN Services & Solutions" text before seal
  const sealTextY = afterTableY + 60;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("For FAMAN Services & Solutions", pageWidth - MARGIN - 150, sealTextY, { align: "left" });

  // Add seal logo below the text
  const logoWidth = 150;
  const logoHeight = 75;
  doc.addImage(
    Seal,
    "PNG",
    pageWidth - MARGIN - logoWidth, // right margin alignment
    sealTextY + 10,  // position below the text
    logoWidth,
    logoHeight
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);

  // ---------------- HELPER FUNCTION ----------------
  function convertNumberToWords(amount) {
    if (!amount || amount === 0) return "Zero";

    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    amount = amount.toString();
    if (amount.length > 9) return "Overflow";

    const n = ("000000000" + amount)
      .substr(-9)
      .match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
    if (!n) return "Zero";

    let str = "";
    str += n[1] != 0 ? (a[Number(n[1])] || b[n[1][0]] + " " + a[n[1][1]]) + " Crore " : "";
    str += n[2] != 0 ? (a[Number(n[2])] || b[n[2][0]] + " " + a[n[2][1]]) + " Lakh " : "";
    str += n[3] != 0 ? (a[Number(n[3])] || b[n[3][0]] + " " + a[n[3][1]]) + " Thousand " : "";
    str += n[4] != 0 ? (a[Number(n[4])] || b[n[4][0]] + " " + a[n[4][1]]) + " Hundred " : "";
    str +=
      n[5] != 0
        ? (str != "" ? "and " : "") + (a[Number(n[5])] || b[n[5][0]] + " " + a[n[5][1]])
        : "";
    return str.trim();
  }
  // ============================================================
  // 🔹 TERMS & CONDITIONS SECTION
  // ============================================================
  // Calculate seal bottom position
  const sealBottomY = sealTextY + 10 + logoHeight;
  
  // Check if current position overlaps with seal, if so move below seal
  const termsStartY = afterTableY + 80;
  
  if (termsStartY < sealBottomY + 20) {
    // If overlapping, position below the seal
    afterTableY = sealBottomY + 20;
  } else {
    // No overlap, use normal spacing
    afterTableY += 80;
  }

  // Section heading
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("TERMS & CONDITIONS:", MARGIN, afterTableY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  // Get terms from quotationData or use default fallback
  const terms = quotationData.QATermsAndCondition?.map(item => item.Name) || [
    ""
  ];

  let currentY = afterTableY + 15;
  const textWidth = pageWidth - MARGIN * 2 - 25;
  const lineSpacing = 8;

  // Draw each term with bullet points
  terms.forEach((term) => {
    // Draw bullet point
    doc.setFont("helvetica", "bold");
    doc.text("•", MARGIN, currentY);
    
    // Draw term text with wrapping
    doc.setFont("helvetica", "normal");
    const wrappedText = doc.splitTextToSize(term, textWidth);
    doc.text(wrappedText, MARGIN + 15, currentY);
    currentY += wrappedText.length * lineSpacing + 8;
  });

  // Add a small gap below
  afterTableY = currentY + 10;

  // ============================================================
  // 🔹 ANNEXURE PAGE(S)
  // ============================================================
  doc.addPage();

  const sections = quotationData.InvoicePrintItems;

  // Group by WorkName
  const groupedSections = sections.reduce((acc, row) => {
    if (!acc[row.QAChildDetailName]) acc[row.QAChildDetailName] = [];
    acc[row.QAChildDetailName].push(row);
    return acc;
  }, {});

  doc.lastAutoTable.finalY = 0;

  // Track all subtotals for grand total calculation
  let allSubtotals = [];
  
  // Track continuous serial number across all sections
  let continuousSlNo = 1;

  // Iterate over grouped sections
  Object.entries(groupedSections).forEach(([QAChildDetailName, rows]) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);

    let startY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 45 : 180;
    
    // Check if there's enough space for header + at least 2 rows (avoid orphan headers)
    const pageHeight = doc.internal.pageSize.getHeight();
    const minSpaceNeeded = 100; // Space needed for header + minimum table rows
    
    if (startY + minSpaceNeeded > pageHeight - 60) {
      doc.addPage();
      startY = 180; // Reset to top margin after header
    }
    
    doc.text(QAChildDetailName, MARGIN, startY);

    const bodyData = rows.map((row) => {
      // Build description: WorkItemName / WorkDetailsName (ItemRemark)
      let description = "";
      
      // Add WorkItemName if available
      if (row.WorkItemName || row.Name) {
        description = normalizeText(row.WorkItemName || row.Name);
      }
      
      // Add WorkDetailsName if available
      if (row.WorkDetailsName || row.Description) {
        description += (description ? " / " : "") + normalizeText(row.WorkDetailsName || row.Description);
      }
      
      // Add ItemRemark if it exists - normalize Unicode fractions to regular text
      if (row.ItemRemark && row.ItemRemark.trim() !== "") {
        const remarkText = normalizeText(row.ItemRemark);
        description += ` (${remarkText})`;
      }
      
      const rowData = [
        continuousSlNo++, // Use continuous serial number and increment
        description,
        row.UOM || "",
        row.Quantity || 0,
        Number(row.Rate || 0).toLocaleString("en-IN"),
        Number(row.TaxableAmount || 0).toLocaleString("en-IN"),
      ];
      return rowData;
    });

    // Calculate subtotal for this section
    const subtotal = rows.reduce((sum, row) => sum + Number(row.TaxableAmount || 0), 0);
    allSubtotals.push(subtotal);

    // Add subtotal row
    bodyData.push([
      "",
      "Sub Total",
      "",
      "",
      "",
      subtotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    ]);

    autoTable(doc, {
      startY: startY + 12,
      theme: "grid",
      margin: { top: 180, left: MARGIN, right: MARGIN, bottom: 60 },
      head: [["Sl.No", "Category", "UOM", "QTY", "Rate", "Total"]],
      body: bodyData,
      headStyles: {
        fillColor: BLUE,
        textColor: [255, 255, 255],
        fontSize: 8,
        halign: "center",
        valign: "middle",
        cellPadding: 3,
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [0, 0, 0],
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 40 },  // Sl.No - increased width
        1: { cellWidth: 'auto', halign: "left" },  // Category (Description) - auto width
        2: { cellWidth: 50 },  // UOM - increased width
        3: { cellWidth: 50 },  // QTY - increased width
        4: { cellWidth: 60 },  // Rate - increased width
        5: { cellWidth: 80 }   // Total - increased width
      },
      didParseCell: (data) => {
        const isSubtotalRow = data.row.index === bodyData.length - 1;

        if (isSubtotalRow) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.font = "helvetica";
          data.cell.styles.fontSize = 8;
          data.cell.styles.fillColor = [255, 255, 204]; // light yellow
          data.cell.styles.textColor = [0, 0, 0]; // black text
          data.cell.styles.halign = (data.column.index === 5) ? "right" : "left";
        } else {
          // Right-align numbers in QTY, Rate, Total
          data.cell.styles.fontSize = 9;
          if ([3, 4, 5].includes(data.column.index)) {
            data.cell.styles.halign = "right";
          }
        }
      },
      didDrawPage: () => addHeader(doc),
    });
  });

  // ============================================================
  // 🔹 GRAND TOTAL (Sum of all subtotals)
  // ============================================================
  const grandTotalAmount = allSubtotals.reduce((sum, subtotal) => sum + subtotal, 0);

  // Add grand total table
  const grandTotalStartY = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: grandTotalStartY,
    theme: "plain",
    margin: { left: MARGIN, right: MARGIN, bottom: 60 },
    body: [
      ["", "GRAND TOTAL", "", "", "", grandTotalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })]
    ],
    bodyStyles: {
      font: "helvetica",
      fontSize: 9, // Increased from 9 to 11
      fontStyle: "bold",
      cellPadding: 5,
      textColor: [0, 0, 0],
      fillColor: [255, 218, 185]
    },
    styles: {
      lineWidth: 0
    },
    tableLineWidth: 0.5,
    tableLineColor: [0, 0, 0],
    columnStyles: {
      0: { cellWidth: 40 },
      1: { halign: "left", cellWidth: 'auto' },
      2: { cellWidth: 50 },
      3: { cellWidth: 50 },
      4: { cellWidth: 60 },
      5: { halign: "right", cellWidth: 100 }
    },
    didDrawPage: () => addHeader(doc),
  });

  // ============================================================
  // 🔹 FOOTER (page numbers)
  // ============================================================
  const addFooter = (doc) => {
    const pageCount = doc.internal.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100);

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      // Left side: website
      doc.text("www.faman.in", MARGIN, pageHeight - 20);

      let quotationRef;
      quotationRef = `${quotationData.InvoicePrint.InvoiceBillNumber}`;

      // Right side: page info with quotation reference
      const pageText = `Page ${i} of ${pageCount} for Invoice #${quotationRef}`;

      doc.text(pageText, pageWidth - MARGIN, pageHeight - 20, { align: "right" });
    }
  };

  // ============================================================
  // 🔹 ADD FOOTER (page numbers on all pages)
  // ============================================================
  addFooter(doc);

  //doc.save("quotation.pdf");
  const blob = doc.output("blob");
  const blobUrl = URL.createObjectURL(blob);
  const win = window.open(blobUrl, "_blank");
  if (win) win.onload = () => {
    win.focus();
    win.print();
  };
};

export default generateInvoiceQuotationPDF;